import { type SnapState, speedSnap, type WarState, warOfCards } from "./games/cards";
import { type CharadesState, emojiCharades } from "./games/charades";
import { type CheckersState, checkers } from "./games/checkers";
import { type CodeBreakerState, codeBreaker } from "./games/codebreaker";
import { type Connect4State, connect4 } from "./games/connect4";
import { type DotsBoxesState, dotsBoxes } from "./games/dotsboxes";
import { type DotsRaceState, dotsRace } from "./games/dotsrace";
import {
	geographyDuel,
	mathSprint,
	noteMatch,
	scienceSorter,
	spellingBee,
	timesTables,
	wouldYouRather,
} from "./games/duels";
import { type FiveInRowState, fiveInRow } from "./games/fiveinrow";
import { type FleetState, fleetBattle } from "./games/fleet";
import { type FlipFourState, flipFour } from "./games/flipfour";
import type { ChoiceDuelState } from "./games/framework/choiceDuel";
import { type GoFishState, goFish, type OldMaidState, oldMaid } from "./games/handgames";
import { type LastCardState, lastCard } from "./games/lastcard";
import { type LogicGridState, logicGrid } from "./games/logicgrid";
import { type MemoryState, memory } from "./games/memory";
import { type MinesweeperState, minesweeperDuel } from "./games/minesweeper";
import { emojiMosaic, type MosaicState } from "./games/mosaic";
import { type NimState, nim } from "./games/nim";
import { type NumberGuessState, numberGuess } from "./games/numberguess";
import { type PegChaseState, pegChase } from "./games/pegchase";
import { type PitRowState, pitRow } from "./games/pitrow";
import { type QuizState, quiz } from "./games/quiz";
import { type ReflexState, reflexTap } from "./games/reflex";
import { type RouteBlockerState, routeBlocker } from "./games/routeblocker";
import { type RpsState, rockPaperScissors } from "./games/rps";
import { type RummyState, rummyRush } from "./games/rummy";
import { type SlidingState, slidingRace } from "./games/sliding";
import { type SnailDashState, snailDash } from "./games/snaildash";
import { type SnakesLaddersState, snakesLadders } from "./games/snakesladders";
import { type SuitRunState, suitRun } from "./games/suitrun";
import { type TicTacToeState, ticTacToe } from "./games/tictactoe";
import { type TowerSwapState, towerSwap } from "./games/towerswap";
import { type TurboTrackState, turboTrack } from "./games/turbotrack";
import { type TypingLadderState, typingLadder } from "./games/typingladder";
import { type WordLadderState, wordLadder } from "./games/wordladder";
import type { GameDefinition, GameId } from "./types";

/** Games built on the shared "both answer at once" framework. */
export type DuelId =
	| "times"
	| "spelling"
	| "sciencesort"
	| "geography"
	| "notematch"
	| "wouldyourather"
	| "mathsprint";

/** Every state the server may be holding. Narrow it with `state.kind`. */
export type GameState =
	| TicTacToeState
	| Connect4State
	| MemoryState
	| DotsBoxesState
	| QuizState
	| ChoiceDuelState<DuelId>
	| NimState
	| FiveInRowState
	| RpsState
	| ReflexState
	| WarState
	| SnapState
	| NumberGuessState
	| MosaicState
	| FleetState
	| FlipFourState
	| CodeBreakerState
	| SlidingState
	| PitRowState
	| CheckersState
	| GoFishState
	| OldMaidState
	| MinesweeperState
	| LastCardState
	| DotsRaceState
	| TypingLadderState
	| CharadesState
	| SuitRunState
	| TowerSwapState
	| WordLadderState
	| LogicGridState
	| RummyState
	| PegChaseState
	| SnakesLaddersState
	| TurboTrackState
	| SnailDashState
	| RouteBlockerState;

/**
 * A game with its specific types erased, which is what the server works with:
 * it never looks inside a state, it only routes moves and ticks.
 *
 * The cast is safe because every definition's state is a member of `GameState`
 * and each `apply` re-validates its own move through `parseMove` first.
 */
export type AnyGameDefinition = GameDefinition<GameState, unknown>;

const DEFINITIONS = [
	// Classics
	ticTacToe,
	connect4,
	dotsBoxes,
	nim,
	fleetBattle,
	flipFour,
	pitRow,
	checkers,
	pegChase,
	snakesLadders,
	// Education
	quiz,
	timesTables,
	spellingBee,
	wordLadder,
	geographyDuel,
	scienceSorter,
	logicGrid,
	// Puzzle
	memory,
	fiveInRow,
	numberGuess,
	codeBreaker,
	slidingRace,
	minesweeperDuel,
	towerSwap,
	routeBlocker,
	// Cards
	warOfCards,
	speedSnap,
	goFish,
	oldMaid,
	lastCard,
	suitRun,
	rummyRush,
	// Racing and reaction
	reflexTap,
	mathSprint,
	typingLadder,
	snailDash,
	turboTrack,
	// Music
	noteMatch,
	// Art
	emojiMosaic,
	dotsRace,
	// Party
	rockPaperScissors,
	wouldYouRather,
	emojiCharades,
] as unknown as AnyGameDefinition[];

export const GAMES: Record<GameId, AnyGameDefinition> = Object.fromEntries(
	DEFINITIONS.map((game) => [game.id, game]),
) as Record<GameId, AnyGameDefinition>;

/** In the order they appear on the "pick a game" screen. */
export const GAME_LIST: readonly AnyGameDefinition[] = DEFINITIONS;

export function getGame(id: GameId): AnyGameDefinition {
	return GAMES[id];
}
